import React, { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { IconButton, CircularProgress } from '@mui/material';
import StopIcon from '@mui/icons-material/Stop';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useTts } from '../../../context/TtsContext';
import { useResources } from '../../../context/ResourceContext';
import { replaceResourceTags } from '../../../utils/textProcessing';
import ConditionalTooltip from '../../common/ConditionalTooltip';

const AudioButton = memo(({ text, messageId }) => {
  const { startTts, stopTts, isPlaying, isPreparing, currentSource } = useTts();
  const { getResourceById } = useResources();
  const messageSourceId = `chat-message-${messageId}`;
  const isThisMessagePlaying = isPlaying && currentSource === messageSourceId;
  const isThisMessageLoading = isPreparing && currentSource === messageSourceId;
  
  const handleTogglePlay = useCallback((e) => {
    e.stopPropagation();
    if (isThisMessageLoading) return;
    if (isThisMessagePlaying) {
      stopTts();
    } else {
      const processedText = replaceResourceTags(text, getResourceById);
      const chunks = processedText.split(/\n\s*\n/).filter(p => p.trim().length > 0);
      const playlist = chunks.map((chunk, index) => ({
        id: `${messageId}-${index}`,
        text: chunk,
      }));
      if (playlist.length > 0) {
        startTts(playlist, messageSourceId);
      }
    }
  }, [text, messageId, startTts, stopTts, isThisMessagePlaying, isThisMessageLoading, messageSourceId, getResourceById]);
  
  return (
    <ConditionalTooltip title={isThisMessagePlaying ? "Stop" : "Read this message aloud"} placement="top">
      <span>
        <IconButton
          size="small"
          onClick={handleTogglePlay}
          disabled={isThisMessageLoading}
          sx={{ p: 0.5, color: 'inherit', opacity: 0.7, '&:hover': { opacity: 1 } }}
        >
          {isThisMessageLoading ? <CircularProgress size={16} color="inherit" /> :
           isThisMessagePlaying ? <StopIcon fontSize="small" /> :
           <PlayArrowIcon fontSize="small" />}
        </IconButton>
      </span>
    </ConditionalTooltip>
  );
});
AudioButton.propTypes = {
  text: PropTypes.string.isRequired,
  messageId: PropTypes.string.isRequired,
};
AudioButton.displayName = 'AudioButton';
export default AudioButton;